<template>
  <form @submit.prevent="validate">
    <Question
      :data="manipulatedQuestionData"
      :enable-answer-lock="validated"
      :hide-header="true"
      :align-text-to-top="true"
      :disable-keyboard="disableKeyboardOnMultipleChoice"
      @answer="onAnswer"
    />
    <transition name="button-transition">
      <ThreeDButton
        v-if="displayValidationButton"
        class="validate-answer-button"
        type="submit"
        icon="question"
      >
        {{ $t('components.lesson.validate-answer-button') }}
      </ThreeDButton>
    </transition>
  </form>
</template>

<script>
import Question from '@/components/Question'
import ThreeDButton from '@/components/ThreeDButton'

import typesetMathMlMixin from '@/mixins/typeset-mathml'

import { clone } from '@/utils/object'
import { QuestionType } from '@/enums'

export default {
  name: 'LessonSectionQuestion',
  mixins: [typesetMathMlMixin],
  components: { Question, ThreeDButton },
  props: { data: Object },
  data() {
    return {
      /**
       * Whether to display the validation button or not
       * @type {Boolean}
       */
      displayValidationButton: false,
      /**
       * The last user answer emitted by the Question component
       * @type {*}
       */
      userAnswer: null,
      /**
       * The last submitted user answer
       * @type {*}
       */
      submittedUserAnswer: null,
      /**
       * A boolean for determining if the current answer has been validated
       * @type {boolean}
       */
      validated: false,
    }
  },
  computed: {
    /**
     * All data regarding the question
     * @returns {Object}
     */
    questionData() {
      return this.data.question[0]
    },
    /**
     * The type of the current question
     * @returns {QuestionType}
     */
    questionType() {
      return this.questionData.Question.type
    },

    /**
     * Disable keyboard functionality on question type Multiple Choice
     * @returns {Boolean}
     */
    disableKeyboardOnMultipleChoice() {
      return this.questionType === QuestionType.MULTIPLE_CHOICE
    },
    /**
     * Data manipulated to make the Question component display it correctly.
     * If a user answer hasn't been submitted, any data about the correct answer is removed.
     * If a user answer has been submitted, data about the submitted answer and its validation is added.
     * @returns {Object}
     */
    manipulatedQuestionData() {
      const questionData = clone(this.questionData)

      switch (this.questionType) {
        case QuestionType.INPUT_FIELD:
          if (!this.submittedUserAnswer) {
            delete questionData.Answer.answer
          } else {
            questionData.UserAnswerInputField = {
              answer: this.submittedUserAnswer,
            }
            questionData.UserAnswer = {
              correct: this.submittedUserAnswer === questionData.Answer.answer,
            }
          }

          break

        case QuestionType.MULTIPLE_CHOICE:
          if (!this.submittedUserAnswer) {
            questionData.answers.forEach(answer => delete answer.Answer.correct)
          } else {
            const correctAnswer = questionData.answers.find(
              answer => answer.Answer.correct
            )

            questionData.UserAnswer = {
              correct: this.submittedUserAnswer === correctAnswer.Answer.id,
            }
          }

          break
      }

      return questionData
    },
  },
  methods: {
    /**
     * Fired every time an answer is entered or changed.
     * @param {*} answer
     */
    onAnswer(answer) {
      this.userAnswer = answer

      switch (this.questionType) {
        case QuestionType.INPUT_FIELD:
          // Display the validation button if there is an answer and the answer string is not empty
          this.displayValidationButton = answer && answer.length > 0
          break
        case QuestionType.MULTIPLE_CHOICE:
          this.validate()
          break
      }
    },
    /**
     * Fired if an answer is submitted for validation.
     */
    validate() {
      this.submittedUserAnswer = this.userAnswer
      this.validated = true
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .question {
    margin-top: -3em;
    font-size: 1em;

    &.has-media {
      max-width: 100%;
    }

    &:focus,
    &:focus-within {
      .question-section {
        box-shadow: none;
      }
    }
  }
  .question-section {
    padding-top: 0;
    padding-left: 0;
    padding-right: 0;
  }
}

.validate-answer-button {
  display: block;
  margin: -1em auto 0;
}

.button-transition-enter-active,
.button-transition-leave-active {
  transition: all var(--transition-fast) ease-out;
}

.button-transition-enter,
.button-transition-leave-to {
  opacity: 0;
  transform: scale(0.7);
}
</style>
